import { useEffect, useRef } from "react";
import { BOARDS_API_BASE_URL } from "../../constants/configuration";

// Hook for the per-user WS channel. Mounted in NotificationsBell so a user
// receives live notifications regardless of which board (if any) they're
// viewing. Auto-reconnects with backoff and surfaces every event via
// `onEvent(event, payload)`.
export default function useUserWs(userId, onEvent) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!userId) return undefined;
    let ws = null;
    let closed = false;
    let retry = 0;
    let timer = null;

    const base = BOARDS_API_BASE_URL.replace(/^http/, "ws").replace(/\/$/, "");
    const url = `${base}/ws/user/${userId}`;

    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(url); } catch { scheduleRetry(); return; }

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg?.type === "event" && handlerRef.current) {
            handlerRef.current(msg.event, msg.payload);
          }
        } catch { /* ignore */ }
      };
      ws.onopen = () => { retry = 0; };
      ws.onclose = () => { if (!closed) scheduleRetry(); };
      ws.onerror = () => { /* onclose handles retry */ };
    };

    const scheduleRetry = () => {
      if (closed) return;
      retry += 1;
      const delay = Math.min(30000, 500 * 2 ** Math.min(retry, 6));
      timer = setTimeout(connect, delay);
    };

    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [userId]);
}
